import {
    ClipboardDocumentCheckIcon,
    NoSymbolIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { EventId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HeaderNameActions } from "components/details-page-v-next/header-name-actions";
import { ShowEditDetailsButton } from "components/details-page-v-next/show-edit-details-button";
import { FilesControl } from "components/files";
import { EntityPageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import {
    ActionsMenu,
    ActionsMenuAction,
    ActionsMenuSection,
} from "components/ui/actions-menu";
import { Badge } from "components/ui/badge";
import { ErrorMessage } from "components/ui/error-message";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { CrmPersona } from "components/ui/persona";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { EventDetailsBlock, hasEventDetails } from "./event-details-inline";
import { eventStatusBadge, formatEventStartsAt } from "./event-format";
import { attendanceSummary, isAwaitingOutcome } from "./event-outcome";
import { EventOutcomeEditor } from "./event-outcome-editor";
import { EventRoster } from "./event-roster";
import { EventSection } from "./event-section";

const EventPageMessage: React.FC<{ children: React.ReactNode }> = props => (
    <Stage>
        <div className="p-4">{props.children}</div>
    </Stage>
);

export const EventPage: React.FC = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const eventsEnabled = useFeature("events");
    const history = useHistory();
    const trpcClient = trpc.useTRPC();
    const [recordingOutcome, setRecordingOutcome] = useState(false);
    const [detailsEditing, setDetailsEditing] = useState(false);
    const query = useQuery({
        ...trpcClient.event.get.queryOptions({ id: eventId as EventId }),
        enabled: eventsEnabled,
    });
    const inviteesQuery = useQuery({
        ...trpcClient.event.listInvitees.queryOptions({ id: eventId as EventId }),
        enabled: eventsEnabled,
    });
    const options = useActivityTypeOptions();

    const location = useLocation();
    const outcomeRequested = new URLSearchParams(location.search).has("outcome");

    const loadedEvent = query.data ?? null;
    const initializedForEventRef = useRef<EventId | null>(null);
    useEffect(() => {
        if (loadedEvent && initializedForEventRef.current !== loadedEvent.id) {
            initializedForEventRef.current = loadedEvent.id;
            setDetailsEditing(!hasEventDetails(loadedEvent));
            if (outcomeRequested) {
                setRecordingOutcome(true);
            }
        }
    }, [loadedEvent, outcomeRequested]);

    const handleChanged = useCallback(async () => {
        await Promise.all([query.refetch(), inviteesQuery.refetch()]);
    }, [query, inviteesQuery]);
    const handleDetailsChanged = useCallback(async () => {
        await query.refetch();
    }, [query]);
    const handleOpenOutcome = useCallback(() => setRecordingOutcome(true), []);
    const handleCloseOutcome = useCallback(() => setRecordingOutcome(false), []);
    const toggleDetailsEditing = useCallback(
        () => setDetailsEditing(current => !current),
        [],
    );
    const backToEvents = useCallback(() => history.push("/calendar/events"), [history]);

    const cancelMutation = useMutation({
        ...trpcClient.event.cancel.mutationOptions(),
        onSuccess: async () => {
            await query.refetch();
        },
    });
    const deleteMutation = useMutation({
        ...trpcClient.event.delete.mutationOptions(),
        onSuccess: backToEvents,
    });
    const handleCancelEvent = useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Cancel event",
            "This tells guests the event is off when they check their RSVP link. It stays in your Past events for your records.",
            "Yes, cancel it",
            "Keep it",
        );
        if (confirmed) {
            cancelMutation.mutate({ id: eventId as EventId });
        }
    }, [cancelMutation, eventId]);
    const handleDeleteEvent = useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Delete event",
            "The guest list and any RSVPs go with it. Attendance already credited to guests stays on their records.",
            "Yes, delete",
            "Keep it",
        );
        if (confirmed) {
            deleteMutation.mutate({ id: eventId as EventId });
        }
    }, [deleteMutation, eventId]);

    if (!eventsEnabled) {
        return (
            <EventPageMessage>
                <ErrorMessage component="EventPage">
                    Events are not enabled for this office.
                </ErrorMessage>
            </EventPageMessage>
        );
    }
    if (query.isPending) {
        return (
            <EventPageMessage>
                <Spinner />
            </EventPageMessage>
        );
    }
    if (query.isError) {
        return (
            <EventPageMessage>
                <ErrorMessage component="EventPage">
                    Sorry, this event could not be found.
                </ErrorMessage>
            </EventPageMessage>
        );
    }

    const event = query.data;
    const badge = eventStatusBadge(event.status);
    const typeName = event.activityTypeId
        ? (options.find(option => option.id === event.activityTypeId)?.name ?? null)
        : null;
    const subtext = [
        typeName,
        formatEventStartsAt(event),
        `${event.eventDuration} minutes`,
    ]
        .filter(part => part !== null)
        .join(" · ");
    const invitees = inviteesQuery.data ?? [];
    const awaitingOutcome = isAwaitingOutcome(event, new Date());
    const attendedCount = invitees.filter(invitee => invitee.attended).length;

    return (
        <Stage>
            <EntityPageHeader>
                <HeaderNameActions
                    name={
                        <div className="flex min-w-0 items-center gap-2">
                            <CrmPersona
                                name={event.title}
                                size="auto"
                                subtext={subtext}
                            />
                            {event.status !== "scheduled" && badge ? (
                                <Badge type={badge.type} value={badge.label} />
                            ) : null}
                        </div>
                    }
                    actions={
                        <ShowEditDetailsButton
                            editing={detailsEditing}
                            onToggle={toggleDetailsEditing}
                        />
                    }
                />
                <div className="flex items-center space-x-4">
                    {awaitingOutcome ? (
                        <PrimaryButton onClick={handleOpenOutcome}>
                            Record outcome
                        </PrimaryButton>
                    ) : null}
                    <ActionsMenu>
                        <ActionsMenuSection>
                            <ActionsMenuAction
                                Icon={ClipboardDocumentCheckIcon}
                                menuHidden={
                                    awaitingOutcome || event.status !== "completed"
                                }
                                onClick={handleOpenOutcome}>
                                {event.status === "completed"
                                    ? "Update outcome"
                                    : "Record outcome"}
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={NoSymbolIcon}
                                menuHidden={event.status !== "scheduled"}
                                onClick={handleCancelEvent}>
                                Cancel event
                            </ActionsMenuAction>
                        </ActionsMenuSection>
                        <ActionsMenuSection>
                            <ActionsMenuAction
                                Icon={TrashIcon}
                                menuHidden={false}
                                onClick={handleDeleteEvent}>
                                Delete event
                            </ActionsMenuAction>
                        </ActionsMenuSection>
                    </ActionsMenu>
                </div>
            </EntityPageHeader>
            <div className="action-bar p-4">
                <div className="space-y-3">
                    {event.status === "cancelled" ? (
                        <InlineBanner type="error">
                            This event is cancelled. It stays here for your records, and
                            guests who open their RSVP link are told it was called off.
                        </InlineBanner>
                    ) : null}
                    {awaitingOutcome ? <OutcomePrompt /> : null}
                    <EventDetailsBlock
                        event={event}
                        editing={detailsEditing}
                        onChanged={handleDetailsChanged}
                    />
                </div>
            </div>
            <DetailPageLayout>
                <div className="grid grid-cols-1 gap-2 pt-4 lg:grid-cols-2 lg:auto-rows-min lg:items-start lg:gap-4">
                    <div className="lg:col-span-2">
                        <EventRoster eventId={event.id} eventStatus={event.status} />
                    </div>
                    <div className="lg:col-span-2">
                        <EventSection title="Attachments" actions={null}>
                            <p className="mt-1 text-xs text-gray-500">
                                Flyers, handouts, and anything else attached here is
                                visible to guests on their RSVP page.
                            </p>
                            <div className="mt-2">
                                <FilesControl
                                    permissions="fixed"
                                    entityId={event.id}
                                    includeSignwise={false}
                                    showSearch={false}
                                />
                            </div>
                        </EventSection>
                    </div>
                    {event.status === "completed" ? (
                        <EventSection
                            title="Outcome"
                            actions={
                                <SecondaryButton onClick={handleOpenOutcome}>
                                    Update outcome
                                </SecondaryButton>
                            }>
                            <p className="mt-2 text-sm text-gray-500">
                                {attendanceSummary(attendedCount, invitees.length)}
                            </p>
                            {event.outcome ? (
                                <p className="mt-2 text-sm text-gray-900">
                                    {event.outcome}
                                </p>
                            ) : (
                                <p className="mt-2 text-sm text-gray-500">
                                    No notes were recorded for this event.
                                </p>
                            )}
                        </EventSection>
                    ) : null}
                </div>
            </DetailPageLayout>
            {recordingOutcome ? (
                <EventOutcomeEditor
                    eventId={event.id}
                    invitees={invitees}
                    currentOutcome={event.outcome}
                    onSaved={handleChanged}
                    onClose={handleCloseOutcome}
                />
            ) : null}
        </Stage>
    );
};

const OutcomePrompt: React.FC = () => (
    <div className="rounded-lg bg-brand-50 px-4 py-3">
        <p className="text-sm font-medium text-gray-900">This event has finished.</p>
        <p className="text-xs text-gray-500">
            Record who came to credit each guest&apos;s record with the activity.
        </p>
    </div>
);
